import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { getAdminSession } from '../../utils/adminSession';
import { addFixtureUrl } from '../../utils/apiEndpoints';

const PageContainer = styled.div`
  max-width: 600px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const Input = styled.input`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;

  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
  }
`;

const Select = styled.select`
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #444;
  background: #111;
  color: #f9fafb;
`;

const HelperText = styled.span`
  font-size: 0.8rem;
  color: var(--gray);
`;

const SubmitButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.1s ease;

  &:hover:enabled {
    background: var(--primary-light);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #12331a;
  color: #c9f7d6;
`;

const ErrorMessage = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: #3a1010;
  color: #ffd1d1;
`;

const ActionsRow = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #222;
  }
`;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const TigersTeamName = 'Sheffield Ice Tigers';
const HomeVenue = 'Ice Sheffield';

const AddFixture: React.FC = () => {
  // Tigers role in the fixture determines which team goes into `homeTeam` / `awayTeam`.
  const [tigersHomeAway, setTigersHomeAway] = useState<'home' | 'away'>('away');
  const [opponent, setOpponent] = useState('');
  const [venue, setVenue] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const session = getAdminSession();

  useEffect(() => {
    // If Tigers are playing at home, venue should default to Ice Sheffield.
    if (tigersHomeAway === 'home') {
      setVenue(HomeVenue);
      return;
    }

    // When switching away, clear the venue only if it is still the home default.
    setVenue((prev) => (prev === HomeVenue ? '' : prev));
  }, [tigersHomeAway]);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    setSubmitState('submitting');
    setErrorMessage(null);

    try {
      if (!session?.apiKey) {
        throw new Error('Session expired. Please log in again.');
      }

      const homeTeam = tigersHomeAway === 'home' ? TigersTeamName : opponent;
      const awayTeam = tigersHomeAway === 'home' ? opponent : TigersTeamName;

      const payload = {
        tableName: 'tigers_fixtures',
        date,
        time,
        homeTeam,
        venue,
        awayTeam,
      };

      const response = await fetch(addFixtureUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': session.apiKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to add fixture');
      }

      setSubmitState('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error while adding fixture';
      setErrorMessage(message);
      setSubmitState('error');
    }
  };

  const resetForm = () => {
    setTigersHomeAway('away');
    setOpponent('');
    setVenue('');
    setDate('');
    setTime('');
    setSubmitState('idle');
    setErrorMessage(null);
  };

  if (submitState === 'success') {
    return (
      <PageContainer>
        <Title>Fixture Added</Title>
        <Message>The fixture was added successfully.</Message>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            Add another fixture
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  if (submitState === 'error') {
    return (
      <PageContainer>
        <Title>Fixture Not Added</Title>
        <ErrorMessage>
          There was a problem adding the fixture.
          {errorMessage ? <div style={{ marginTop: '0.5rem' }}>{errorMessage}</div> : null}
        </ErrorMessage>
        <ActionsRow>
          <SecondaryButton type="button" onClick={resetForm}>
            Try again
          </SecondaryButton>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Add a Fixture</Title>
      <Form onSubmit={handleSubmit}>
        <FieldGroup>
          <Label htmlFor="tigersHomeAway">Tigers Fixture Location</Label>
          <Select
            id="tigersHomeAway"
            value={tigersHomeAway}
            onChange={(e) => setTigersHomeAway(e.target.value as 'home' | 'away')}
            required
          >
            <option value="away">Tigers Away</option>
            <option value="home">Tigers Home</option>
          </Select>
          <HelperText>
            {tigersHomeAway === 'home'
              ? `Venue will default to ${HomeVenue}.`
              : 'Enter the venue for Tigers away games.'}
          </HelperText>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="opponent">{tigersHomeAway === 'home' ? 'Away Team' : 'Home Team'} (Opponent)</Label>
          <Input
            id="opponent"
            type="text"
            value={opponent}
            onChange={(e) => setOpponent(e.target.value)}
            required
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="venue">Venue</Label>
          <Input
            id="venue"
            type="text"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            required
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="date">Date</Label>
          <Input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
          <HelperText>Format: YYYY-MM-DD</HelperText>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="time">Time (24-hour)</Label>
          <Input
            id="time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            required
          />
          <HelperText>Format: HH:MM (24-hour clock)</HelperText>
        </FieldGroup>

        <SubmitButton type="submit" disabled={submitState === 'submitting'}>
          {submitState === 'submitting' ? 'Submitting…' : 'Add Fixture'}
        </SubmitButton>
        <ActionsRow>
          <LinkButton to="/admin">Back to admin</LinkButton>
        </ActionsRow>
      </Form>
    </PageContainer>
  );
};

export default AddFixture;

